import { useEffect, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { apiClient } from '../../lib/api-client';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';

interface GraphApiStatus {
  accessGranted: boolean;
  grantedAt?: string;
  grantedBy?: string;
  pendingRequest: boolean;
  requestedAt?: string;
  requestedBy?: string;
  adminEmail?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tenantCode: string | null;
  tenantName?: string;
  tenantDomains?: string[];
  currentStatus?: GraphApiStatus;
  canApprove?: boolean;
}

export function GraphApiRequestDialog({ 
  open, 
  onClose, 
  onSuccess, 
  tenantCode, 
  tenantName,
  tenantDomains,
  currentStatus,
  canApprove = false,
}: Props) {
  const [adminEmail, setAdminEmail] = useState(currentStatus?.adminEmail || '');
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAdminEmail(currentStatus?.adminEmail || '');
    setSelectedDomain((tenantDomains && tenantDomains.length > 0) ? tenantDomains[0] : '');
    setClientId('');
    setClientSecret('');
    setTenantId('');
    setError(null);
    setSuccess(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantCode]);

  const handleRequest = async () => {
    if (!tenantCode || !adminEmail.trim()) {
      setError('Admin email is required');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await apiClient.requestGraphApiAccess(tenantCode, adminEmail.trim(), selectedDomain || undefined);
      setSuccess('Graph API access request submitted. An email will be sent to the tenant admin.');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (e: any) {
      setError(e?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!tenantCode) return;
    if (!clientId.trim() || !clientSecret.trim() || !tenantId.trim()) {
      setError('clientId, clientSecret, and tenantId are required');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await apiClient.approveGraphApiAccess(
        tenantCode,
        { clientId: clientId.trim(), clientSecret: clientSecret.trim(), tenantId: tenantId.trim() },
        selectedDomain || undefined
      );
      setSuccess('Graph API access approved.');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (e: any) {
      setError(e?.message || 'Failed to approve request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="lg">
        <DialogHeader>
          <DialogTitle>
            Graph API Access - {tenantName || tenantCode?.toUpperCase()}
          </DialogTitle>
          <DialogDescription>
            Microsoft Graph API access enables personalized Teams notifications for users.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200 mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-green-800 dark:text-green-200 mb-4">
              {success}
            </div>
          )}

          {/* Current Status */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Current Status</h4>
            
            {currentStatus?.accessGranted ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-700 dark:text-green-300">Access Granted</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Granted by {currentStatus.grantedBy} on {formatDate(currentStatus.grantedAt)}
                  </p>
                </div>
              </div>
            ) : currentStatus?.pendingRequest ? (
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Request Pending</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Requested by {currentStatus.requestedBy} on {formatDate(currentStatus.requestedAt)}
                  </p>
                  {currentStatus.adminEmail && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Admin email: {currentStatus.adminEmail}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Not Configured</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Graph API access has not been requested for this tenant.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Request Form (if not granted and no pending request) */}
          {!currentStatus?.accessGranted && !currentStatus?.pendingRequest && (
            <div className="space-y-4">
              {Array.isArray(tenantDomains) && tenantDomains.length > 1 && (
                <div>
                  <label htmlFor="graph-domain" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Domain
                  </label>
                  <select
                    id="graph-domain"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm"
                    value={selectedDomain}
                    onChange={(e) => setSelectedDomain(e.target.value)}
                  >
                    {tenantDomains.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Graph API access is granted per domain; multiple portal tenants can share a domain.
                  </p>
                </div>
              )}
              <div>
                <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tenant Admin Email
                </label>
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="admin@tenant-domain.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter the email of the tenant administrator who can grant Graph API consent.
                </p>
              </div>
            </div>
          )}

          {/* Approve option for pending requests */}
          {currentStatus?.pendingRequest && canApprove && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                Enter the domain app credentials (stored in Vault) after Entra admin consent has been granted.
              </p>
              {Array.isArray(tenantDomains) && tenantDomains.length > 1 && (
                <div className="mb-3">
                  <label htmlFor="graph-domain-approve" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Domain
                  </label>
                  <select
                    id="graph-domain-approve"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm"
                    value={selectedDomain}
                    onChange={(e) => setSelectedDomain(e.target.value)}
                  >
                    {tenantDomains.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label htmlFor="graph-client-id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client ID
                  </label>
                  <Input id="graph-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="graph-client-secret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client Secret
                  </label>
                  <Input id="graph-client-secret" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="graph-tenant-id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tenant ID
                  </label>
                  <Input id="graph-tenant-id" value={tenantId} onChange={(e) => setTenantId(e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={onClose} disabled={submitting} variant="secondary">
            {currentStatus?.accessGranted ? 'Close' : 'Cancel'}
          </Button>
          
          {!currentStatus?.accessGranted && !currentStatus?.pendingRequest && (
            <Button 
              onClick={handleRequest} 
              disabled={submitting || !adminEmail.trim()}
            >
              {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              {submitting ? 'Submitting...' : 'Request Access'}
            </Button>
          )}

          {currentStatus?.pendingRequest && canApprove && (
            <Button 
              onClick={handleApprove} 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              {submitting ? 'Approving...' : 'Approve Access'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
